import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from 'features/ai-assistant/constants';
import { useShowAlert } from 'hooks';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import { ALT_API_CLIENT } from 'services/API';
import strings from 'strings';
import { UIStatus } from 'UI/constants/status';

const MIN_REQUIRED_QUERY_LENGTH = 3;
const normalizeQuery = value => value.trim().toLowerCase();

export const useSearchModal = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleOpenSearch = () => setIsSearchOpen(true);
  const handleCloseSearch = () => setIsSearchOpen(false);

  return { isSearchOpen, handleOpenSearch, handleCloseSearch };
};

export const useSearchConversation = ({
  apiInstance = ALT_API_CLIENT,
  endpoint = ENDPOINTS.sessionSearch,
  query,
  debounceMs = 400
}) => {
  const { showAlert } = useShowAlert();
  const { nextSignal, abort } = useLatestRequestSignal();

  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [uiStatus, setUiStatus] = useState(UIStatus.Default);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(normalizeQuery(query));
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [query, debounceMs]);

  const getResults = useCallback(
    async term => {
      const signal = nextSignal();

      try {
        setUiStatus(UIStatus.Loading);
        setResults([]);

        const response = await apiInstance.get(endpoint, {
          signal,
          params: {
            term
          }
        });
        const data = response.data?.results
          ? response.data.results.filter(result => !!result.sessionId)
          : [];

        setResults(data);

        const isEmpty = data.length === 0;
        setUiStatus(isEmpty ? UIStatus.Empty : UIStatus.Success);
      } catch (error) {
        if (axios.isCancel(error)) return;

        setUiStatus(UIStatus.Error);
        showAlert({
          error,
          title: strings.formatString(strings.ai.generators.assistant.errors.failedAction, {
            action: 'search'
          })
        });
      }
    },
    [apiInstance, endpoint, nextSignal, showAlert]
  );

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < MIN_REQUIRED_QUERY_LENGTH) {
      abort();
      setResults([]);
      setUiStatus(UIStatus.Default);
      return;
    }

    getResults(debouncedQuery);
  }, [debouncedQuery, getResults, abort]);

  return {
    results,
    uiStatus
  };
};
